// Team KPI page: Team KPI Page.dc.html from the design project, in the layout every view shares (the packet, the
// dashboard's windows and the live page): the KPI with its sample beneath | value and where it stands (one game: its
// percentile of league team-games; a window: its rank) | goal and the rank it represents | the season level and its
// rank | goals met over the season, or the window's games (Trevor 2026-10-10, 10-27: the same display in every view, no bar). `windowed` (the dashboard) swaps the Tonight column for the selected window, as the design's
// dashboard note says. `dashboard` (the dashboard and the live page, screen only) lets the sheet grow; the packet (PA1
// prints it) keeps one letter page with tighter spacing (PACKET).
import { CSSProperties, Fragment } from "react";
import {
  liveMeta,
  buildTeam,
  gameMeta,
  GameMeta,
  packetPageLabel,
  periodLabel,
  rankNote,
  windowTitle,
} from "./kpiLib";
import { Footer, Legend, Masthead, ValueBlock, WindowHead } from "./common";
import type { KpiResponse } from "./types";

const DISP = "var(--pr-disp)";
const headSt: CSSProperties = {
  display: "grid",
  columnGap: 9,
  background: "#f5f5f5",
  borderTop: "2px solid #000",
  borderBottom: "2px solid #4c4c4c",
  padding: "3px 6px 2px",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "#4c4c4c",
};
// KPI with its sample beneath | value + percentile (one game) or rank | goal + rank | season + rank | goals met.
const COLS = "176px 132px 98px 100px minmax(0,1fr)";
const rankSt: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "#6b6864",
  whiteSpace: "nowrap",
};
const rowSt: CSSProperties = {
  display: "grid",
  columnGap: 9,
  alignItems: "center",
  padding: "0 6px",
  borderBottom: "1px solid #e6e4e1",
  lineHeight: 1.1,
};
// Vertical rhythm. The screen pages grow with their rows. The packet's 20 rows, 6 groups and footnotes must fit one
// letter sheet with 0.3in to spare (scripts/print_fit.py --content-bottom 10.66, unshrunk): its two-line name sets
// tighter so it centres on the value box in a 26px row, and the group heads and summary close up.
const SCREEN = {
  row: 36,
  nameLh: 1.15,
  sampleMt: 2,
  groupPad: ["6px 0 4px", "14px 0 4px"],
  sideMt: 7,
  headMt: 4,
  sumPad: "5px 0 6px",
};
const PACKET = {
  row: 26,
  nameLh: 1.05,
  sampleMt: 1,
  groupPad: ["3px 0 2px", "6px 0 2px"],
  sideMt: 4,
  headMt: 2,
  sumPad: "3px 0 4px",
};

export default function TeamKpiPage({
  data,
  windowed = false,
  dashboard = false,
  pageLabel = packetPageLabel("Team KPIs", "team"),
  goalsLabel,
}: {
  data: KpiResponse;
  windowed?: boolean;
  /** A screen page that grows with its rows (the dashboard, the live page); the packet leaves it off and keeps one
   * letter sheet (PA1 prints it). */
  dashboard?: boolean;
  pageLabel?: string;
  /** A past season on the dashboard, measured against this season's list: "2026-27 goals" (Trevor 2026-10-07). */
  goalsLabel?: string;
}) {
  const game = data.meta.game!;
  const g0: GameMeta = gameMeta(game, data.meta.porGamesToDate);
  // A live page has no result yet: the period stands where W / L would.
  const g: GameMeta = liveMeta(g0, data.meta.live?.throughPeriod);
  const t = buildTeam(data.team || [], windowed);
  const live = data.meta.live;
  const liveLabel = live ? periodLabel(live.throughPeriod) : null;
  const winLabel =
    (windowed && data.team?.find((k) => k.window)?.window?.label) || liveLabel;
  const tonight = winLabel || "Tonight";
  const sides = [
    { title: "Offense", tally: t.offTally, groups: t.off },
    { title: "Defense", tally: t.defTally, groups: t.def },
  ];
  const seen: Record<string, boolean> = {};
  const wt = windowed
    ? windowTitle(data.meta.window?.selected, data.meta.windowSummary)
    : null;
  const win: WindowHead | null = wt
    ? { ...wt, summary: data.meta.windowSummary }
    : null;
  const V = dashboard ? SCREEN : PACKET;
  // The Season window's box already is the season to date: the column would repeat it (as on the player page).
  const seasonWin = windowed && data.meta.window?.selected?.kind === "season";
  // A preseason game's own view shows its preseason to date (the API labels the block).
  const seasonLabel =
    data.team?.find((k) => k.seasonToDate)?.seasonToDate?.label || "Season";
  const cols = COLS;
  const head: CSSProperties = {
    ...headSt,
    gridTemplateColumns: cols,
    marginTop: V.headMt,
  };
  const row: CSSProperties = {
    ...rowSt,
    gridTemplateColumns: cols,
    height: V.row,
  };
  return (
    <section
      className={dashboard ? "page tall" : "page"}
      data-screen-label="Team KPIs"
    >
      <Masthead
        g={g}
        who={pageLabel}
        kicker="Portland Trail Blazers · Style of Play"
        title="Team KPIs"
        report={live ? "Live KPIs" : undefined}
        win={win}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 18,
          borderTop: "2px solid #000",
          borderBottom: "1px solid #cfccc8",
          padding: V.sumPad,
        }}
      >
        <div
          style={{
            fontFamily: DISP,
            fontWeight: 800,
            fontSize: 42,
            lineHeight: 0.9,
            whiteSpace: "nowrap",
          }}
        >
          {t.metN} of {t.metD}
        </div>
        <div
          style={{
            fontFamily: DISP,
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {goalsLabel ? `${goalsLabel} met` : "Goals met"}
          <br />
          {winLabel ? winLabel : "tonight"}
        </div>
        <div style={{ display: "flex", gap: 14, marginLeft: 8 }}>
          {[
            [t.offTally, "Offense"],
            [t.defTally, "Defense"],
          ].map(([v, l]) => (
            <div
              key={l}
              style={{
                borderLeft: "1px solid #cfccc8",
                paddingLeft: 12,
                lineHeight: 1,
              }}
            >
              <div style={{ fontFamily: DISP, fontWeight: 800, fontSize: 24 }}>
                {v}
              </div>
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "#6b6864",
                  fontWeight: 700,
                  marginTop: 3,
                }}
              >
                {l}
              </div>
            </div>
          ))}
        </div>
        <Legend />
      </div>

      {sides.map((s) => (
        <Fragment key={s.title}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 12,
              marginTop: V.sideMt,
            }}
          >
            <span
              style={{
                fontFamily: DISP,
                fontWeight: 800,
                fontSize: 26,
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              {s.title}
            </span>
            <span
              style={{
                fontFamily: DISP,
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1,
              }}
            >
              {s.tally}{" "}
              <span
                style={{
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  color: "#6b6864",
                }}
              >
                goals met
              </span>
            </span>
          </div>
          <div style={head}>
            <span>KPI · sample</span>
            <span style={{ textAlign: "center" }}>{tonight}</span>
            <span style={{ whiteSpace: "nowrap" }}>{goalsLabel || "Goal"}</span>
            <span>{seasonWin ? "" : seasonLabel}</span>
            <span style={{ whiteSpace: "nowrap" }}>
              {windowed && winLabel && !seasonWin
                ? `Met · ${winLabel}`
                : seasonLabel === "Preseason"
                  ? "Met · preseason"
                  : "Met · season"}
            </span>
          </div>
          {s.groups.map((grp) => {
            const pad = V.groupPad[seen[grp.side] ? 1 : 0];
            seen[grp.side] = true;
            return (
              <Fragment key={grp.group}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: pad,
                    lineHeight: 1,
                  }}
                >
                  <span
                    style={{
                      fontFamily: DISP,
                      fontWeight: 700,
                      fontSize: 16,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                      lineHeight: 1,
                    }}
                  >
                    {grp.group}
                  </span>
                  <span
                    style={{ flex: 1, height: 1.5, background: "#1a1a1a" }}
                  />
                </div>
                {grp.kpis.map((k) => (
                  <div key={k.label} style={row} title={k.definition}>
                    <span style={{ minWidth: 0, lineHeight: V.nameLh }}>
                      <span
                        style={{
                          display: "block",
                          fontWeight: 700,
                          fontSize: 12.5,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {k.label}{" "}
                        <span
                          style={{
                            color: "#6b6864",
                            fontWeight: 400,
                            textTransform: "none",
                            fontSize: 10.5,
                          }}
                        >
                          {k.unit}
                        </span>
                      </span>
                      <span
                        style={{
                          display: "block",
                          fontSize: 10,
                          color: "#6b6864",
                          marginTop: V.sampleMt,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {k.game.text}
                      </span>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      <ValueBlock
                        m={k.game.mark}
                        value={k.game.v}
                        padding="2px 7px 2px 6px"
                        width="74px"
                        fontSize={17}
                      />
                      <span style={rankSt}>{k.game.rank}</span>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                        {k.goal}
                      </span>
                      <span style={rankSt}>{k.goalRank}</span>
                    </span>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {!seasonWin && k.raw.seasonToDate && (
                        <>
                          <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                            {k.std.v}
                          </span>
                          <span style={rankSt}>{k.std.rank}</span>
                        </>
                      )}
                    </span>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {k.hit.short || "—"}
                    </span>
                  </div>
                ))}
              </Fragment>
            );
          })}
        </Fragment>
      ))}
      {live && (
        <div className="fn">
          Live: {liveLabel?.toLowerCase()}, computed{" "}
          {new Date(live.computedAt.replace(" ", "T") + "Z").toLocaleTimeString(
            "en-US",
            { hour: "numeric", minute: "2-digit" },
          )}{" "}
          from CTG's live feed. KPIs reading "after the game" need CTG's
          end-of-game data.
        </div>
      )}
      <div className="fn">{rankNote(data.meta.teamRank, !win)}</div>
      <Footer
        left={win ? "Portland Trail Blazers · KPI Dashboard" : undefined}
        right={`Team KPIs · ${win ? win.line : g.line}`}
      />
    </section>
  );
}
